import React, { useEffect, useRef, useState } from 'react';
import { UserPlus, ShoppingBag, BadgePercent, Sparkles } from 'lucide-react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folies 25 + 26 — "Der einfache Weg".
 * Dynamic layout: 3 luxury step nodes connected by an animated gold path.
 * Each node has an orbital ring, pulsing icon, tilt-on-hover card,
 * animated number counter and a shimmer trail.
 */
const ICONS = {
  signup: UserPlus,
  cart: ShoppingBag,
  discount: BadgePercent,
};

/**
 * Simple in-view hook — fires once when the element crosses the viewport.
 */
function useInView(threshold = 0.35) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function StepNode({ step, index, active, delayMs }) {
  const Icon = ICONS[step.icon] || UserPlus;
  return (
    <div
      className={`einfach-node ${active ? 'is-active' : ''}`}
      data-testid={`step-${index}`}
      style={{ '--delay': `${delayMs}ms` }}
    >
      {/* Orbital ring around the icon disc + central disc inside */}
      <div className="einfach-orbit" aria-hidden>
        <span className="einfach-orbit-ring einfach-orbit-ring--slow" />
        <span className="einfach-orbit-ring einfach-orbit-ring--fast" />
        <span className="einfach-orbit-glow" />
        <div className="einfach-disc">
          <Icon strokeWidth={1.4} className="einfach-disc-icon" />
          <span className="einfach-disc-corner einfach-disc-corner--tl" />
          <span className="einfach-disc-corner einfach-disc-corner--tr" />
          <span className="einfach-disc-corner einfach-disc-corner--bl" />
          <span className="einfach-disc-corner einfach-disc-corner--br" />
        </div>
      </div>

      {/* Big number above/below */}
      <div className="einfach-num font-display" aria-hidden>{step.n}</div>

      {/* Card content */}
      <div className="einfach-card">
        <div className="einfach-card-shine" aria-hidden />
        <h3 className="einfach-title font-display">{step.title}</h3>
        <p className="einfach-body font-ui">{step.body}</p>
      </div>

      {/* Sparkle badge on completion */}
      <span className="einfach-spark" aria-hidden>
        <Sparkles className="w-3.5 h-3.5" />
      </span>
    </div>
  );
}

export default function SectionEinfacherWeg() {
  const { t } = useT();
  const [wrapRef, inView] = useInView(0.28);
  const steps = t.einfacherWeg.steps || [];

  return (
    <SectionShell id="einfacher-weg" className="" centerContent>
     <div>
      <Reveal>
        <div className="overline mb-6">{t.einfacherWeg.overline}</div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-3 text-white max-w-[22ch]">
          {t.einfacherWeg.title}
        </h2>
        <p className="font-ui text-white/45 text-sm tracking-[0.16em] uppercase mb-4">
          {t.einfacherWeg.captionParts ? (
            <>
              {t.einfacherWeg.captionParts.count} · <span className="text-white/70">{t.einfacherWeg.captionParts.middle}</span> · {t.einfacherWeg.captionParts.end}
            </>
          ) : (
            t.einfacherWeg.caption || '3 · Schritte · zum Erfolg'
          )}
        </p>
      </Reveal>

      <div
        ref={wrapRef}
        className={`einfach-wrap ${inView ? 'is-live' : ''}`}
        data-testid="einfach-flow"
      >
        {/* Floating gold micro-particles */}
        <div className="einfach-particles" aria-hidden>
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="einfach-particle" style={{ '--i': i }} />
          ))}
        </div>

        {/* Animated dashed gold path connecting the three nodes */}
        <svg
          className="einfach-path"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="einfachGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor="#f2d979" stopOpacity="0" />
              <stop offset="12%" stopColor="#f2d979" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#d4af37" stopOpacity="1" />
              <stop offset="88%" stopColor="#f2d979" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f2d979" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* subtle base track */}
          <path
            d="M 60 60 C 300 10, 500 110, 600 60 S 900 10, 1140 60"
            stroke="rgba(212,175,55,0.14)"
            strokeWidth="1"
            fill="none"
          />
          {/* animated gold stroke */}
          <path
            className="einfach-path-stroke"
            d="M 60 60 C 300 10, 500 110, 600 60 S 900 10, 1140 60"
            stroke="url(#einfachGrad)"
            strokeWidth="1.4"
            fill="none"
            strokeDasharray="6 8"
          />
        </svg>

        {/* Nodes */}
        <div className="einfach-grid">
          {steps.map((s, i) => (
            <StepNode
              key={i}
              step={s}
              index={i}
              active={inView}
              delayMs={i * 260}
            />
          ))}
        </div>
      </div>
     </div>
    </SectionShell>
  );
}
